import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Rss, ExternalLink } from "lucide-react";
import { Seo } from "@/site/seo";
import { Heading } from "@/site/components/Section";
import { SITE } from "@/site/content";
import { track } from "@/site/analytics";

/**
 * Human-readable landing for the RSS feed. Browsers render blog.xml as a bare
 * XML tree (there is no built-in reader any more), which looks broken to
 * anyone who is not already a feed-reader user. So every "RSS" link on the
 * site points here; the raw feed is one deliberate click further.
 */
export default function SubscribePage() {
  const feed = `${SITE.url}/blog.xml`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(feed); } catch { /* fall through */ }
    setCopied(true); track("feed_copy"); setTimeout(() => setCopied(false), 1800);
  };

  const readers = [
    { name: "Feedly", how: "Click “+ Follow sources”, paste the address, choose a folder." },
    { name: "Inoreader", how: "“Add new” → “Feed”, paste the address." },
    { name: "NetNewsWire (Mac / iOS)", how: "File → New Feed, paste the address. Free and open source." },
    { name: "Slack", how: "In any channel type /feed subscribe followed by the address. New posts appear as messages." },
    { name: "Outlook (desktop)", how: "Right-click “RSS Feeds” in the folder list → “Add a New RSS Feed…”, paste the address." },
  ];

  return (
    <>
      <Seo title="Subscribe to the Postern blog" description="Get each new post in your feed reader or a Slack channel. No email list, nothing to unsubscribe from." path="/blog/subscribe" />
      <section className="section">
        <div className="wrap" style={{ maxWidth: 760 }}>
          <nav aria-label="Breadcrumb" className="crumbs"><Link to="/blog">Blog</Link><span aria-hidden="true">/</span><span>Subscribe</span></nav>
          <Heading eyebrow="Subscribe" title="New posts, wherever you already read." lede="We publish most weeks and we do not run an email list. The blog has an RSS feed, which any feed reader or a Slack channel can follow. Copy the address below into the one you use." />

          <div className="feedbox" data-testid="feed-box">
            <div className="feedbox__row">
              <Rss size={18} aria-hidden="true" />
              <code className="feedbox__url" data-testid="feed-url">{feed}</code>
              <button type="button" className="btn btn--primary btn--sm" onClick={copy} data-testid="feed-copy">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy address"}</button>
            </div>
            <div className="hint" style={{ marginTop: 10 }}>This is a feed address, not a web page. Opening it directly shows the raw XML your reader consumes — that is normal.</div>
          </div>

          <h2 className="h3" style={{ marginTop: 36, marginBottom: 14 }}>Where to paste it</h2>
          <div className="post-list">
            {readers.map((r) => (
              <div key={r.name} className="post post--sm" style={{ cursor: "default", transform: "none" }} data-testid={`reader-${r.name.split(" ")[0].toLowerCase()}`}>
                <div className="post__title" style={{ fontSize: 16, marginBottom: 4 }}>{r.name}</div>
                <div className="post__desc" style={{ fontSize: 14 }}>{r.how}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 32, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", fontSize: 14, color: "var(--muted)" }}>
            <a href="/blog.xml" className="mono" style={{ display: "inline-flex", gap: 6, alignItems: "center" }} data-testid="feed-raw">View the raw feed <ExternalLink size={13} /></a>
            <span aria-hidden="true">·</span>
            <Link to="/blog">Back to the blog</Link>
          </div>
        </div>
      </section>
    </>
  );
}
